import { FC, useState } from 'react';
import { Modal, Select, Button } from '@crisellevaldez/speculo-ui';
import { useCallAnalysis } from '../../../hooks/dashboard/calls/useCallAnalysis';
import { getOutcomeDescription } from '../../../utils/badgeStyles';

// The 11 canonical call outcomes the person can set when correcting a call.
// Values are lowercase snake_case to match how the backend normalizes and
// compares outcomes; descriptions come from the shared OUTCOME_DESCRIPTIONS so
// the dropdown and the outcome-badge tooltips never drift. `conversation` keeps
// its product label "Verified".
const OUTCOME_OPTIONS: { value: string; label: string }[] = [
  { value: 'conversation', label: 'Verified' },
  { value: 'nurture', label: 'Nurture' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'do_not_call', label: 'Do Not Call' },
  { value: 'alert', label: 'Alert' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'no_contact', label: 'No Contact' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'incomplete', label: 'Incomplete' },
];

interface EditCallAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  callId: string;
  currentOutcome: string;
  onSuccess: (newOutcome: string) => void;
}

export const EditCallAnalysisModal: FC<EditCallAnalysisModalProps> = ({
  isOpen,
  onClose,
  callId,
  currentOutcome,
  onSuccess,
}) => {
  // Stored outcomes are messy (Title_Case, spaces) while the options are
  // lowercase snake_case; normalize so the current value preselects instead of
  // opening blank. The backend normalizes the same way when it compares.
  const [selectedOutcome, setSelectedOutcome] = useState(
    (currentOutcome ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_')
  );
  const { updateCallAnalysis, isUpdating } = useCallAnalysis();

  const handleChange = (newValue: string | string[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setSelectedOutcome(value);
  };

  const handleConfirm = async () => {
    const success = await updateCallAnalysis(callId, selectedOutcome);
    if (success) {
      onSuccess(selectedOutcome);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <Modal.Header>
        <h2 className="text-lg font-semibold">Edit Call Analysis</h2>
      </Modal.Header>
      <Modal.Body>
        <Select
          options={OUTCOME_OPTIONS}
          value={selectedOutcome}
          onChange={handleChange}
          disabled={isUpdating}
          placeholder="Select outcome"
          className="w-full"
          renderOption={(option) => {
            const description = getOutcomeDescription(option.value);
            return (
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">{option.label}</span>
                {description && (
                  <span className="text-xs text-muted-foreground leading-snug">{description}</span>
                )}
              </div>
            );
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isUpdating} isLoading={isUpdating}>
            Confirm
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
